// WebXR / ARCore driver — Path 2 from the diff-diag runbook.
//
// Replaces XR8 entirely. Uses the browser's native WebXR Device API,
// which on Chrome-Android delegates to ARCore — Google's native SLAM
// engine, categorically better than XR8 on Pixel-class devices and
// tuned for the same hardware. No 8thwall licensing, no dashboard,
// no appKey allowlist; just the W3C standard.
//
// Implements the same `ARDriver` protocol as `RealXR8Driver` so it
// drops into `WebXrWheelRenderer` with no scene-graph changes.
// Tracking-status lifecycle is mapped onto the same ARSessionState
// stream the UI already subscribes to.
//
// Browser-only by design (no vitest coverage — navigator.xr is not
// in node). Validate on a real Pixel 7 / Chrome 117+ session.

import * as THREE from 'three'
import {
  arSessionState,
  type ARSessionState,
} from '@/models/placement-state'
import type {
  HitResult,
  ScreenPoint,
} from '@/ar/wheel-renderer'
import { PREVIEW_DISTANCE_M, type ARDriver } from '@/ar/webxr-wheel-renderer'
import {
  computeDepthWorldPoint,
  isUsableDepthMeters,
  shouldSampleDepth,
} from '@/ar/depth-placement'
import {
  SURFACE_YAW_SAMPLE_OFFSET_NVC,
  worldPointFromDepthSample,
} from '@/ar/surface-yaw'
import { atoLog } from '@/services/ato-log'
import { withClassicXrLayer } from '@/ar/with-classic-xr-layer'
import { readGrantedDepthConfig } from '@/ar/read-granted-depth-config'

/** Per-plane info exposed to the renderer + HUD. Snapshot each frame
 *  from ARCore's detected planes; keyed by a stable id (using the
 *  XRPlane object reference as the identity — a plane's identity is
 *  stable across frames per the WebXR spec, so we can persist grid
 *  meshes across frames using it). */
export interface DetectedPlaneInfo {
  /** Stable id for this plane across frames (indexed off the XRPlane
   *  object reference — assigned monotonically the first time we see
   *  each plane so we get short, HUD-friendly ids like "P1", "P2"). */
  readonly id: string
  /** 'horizontal' (floors, tables, ceilings) or 'vertical' (walls,
   *  windows, doors, screens). */
  readonly orientation: 'horizontal' | 'vertical'
  /** Current world-space position of the plane's origin. */
  readonly position: { x: number; y: number; z: number }
  /** Current world-space orientation as a quaternion. Used to align
   *  visualizations with the actual plane normal. */
  readonly quaternion: { x: number; y: number; z: number; w: number }
  /** Polygon vertices in PLANE-LOCAL space (Y=0). Multiply the plane's
   *  world matrix through them to render in world coords. */
  readonly polygon: ReadonlyArray<{ x: number; z: number }>
  /** Rough area in square meters (shoelace over the polygon). */
  readonly areaM2: number
}

export interface DetectedPlanesStore {
  getState(): DetectedPlaneInfo[]
  subscribe(listener: () => void): () => void
}

/** Classifier output for the surface the camera-forward ray is
 *  currently hitting, derived from the hit-test pose's world normal.
 *  Works on ANY WebXR device that supports hit-test — does not
 *  require the (widely-unsupported) plane-detection feature. */
export type SurfaceClass = 'wall' | 'floor' | 'ceiling' | 'skew' | 'none'

/** Y-component of the surface normal (in world coords) is our
 *  classification signal. Absolute Y near 1 → horizontal surface;
 *  Y near 0 → vertical surface. Numbers picked with 15° of tolerance
 *  from perfect axis alignment — anything outside is "skew" and
 *  filtered out of placement.
 *  cos(15°) ≈ 0.966 → walls: |normal.y| < ~0.26
 *  sin(15°) ≈ 0.259 → floors/ceilings: |normal.y| > ~0.966
 *  Between 0.26 and 0.966 → skew (e.g., ramp, sloped surface). */
export const WALL_MAX_NORMAL_Y = 0.26
export const HORIZONTAL_MIN_NORMAL_Y = 0.85

/** Wall-only tracking gate: driver's cachedHit position is refreshed
 *  IFF the current hit-surface is a wall. Non-wall hits leave
 *  cachedHit at the previously-tracked wall position — analogous to
 *  a mouse cursor whose last on-screen position is held when the
 *  cursor leaves the window. Pinned by shouldTrackHit tests. */
export function shouldTrackHit(surface: SurfaceClass): boolean {
  return surface === 'wall'
}

/** Classifies a surface from the Y component of its world-space normal.
 *  Pure — testable without WebXR. */
export function classifySurface(normalWorldY: number): SurfaceClass {
  if (normalWorldY > HORIZONTAL_MIN_NORMAL_Y) return 'floor'
  if (normalWorldY < -HORIZONTAL_MIN_NORMAL_Y) return 'ceiling'
  if (Math.abs(normalWorldY) < WALL_MAX_NORMAL_Y) return 'wall'
  return 'skew'
}

/** Applies a quaternion to the (0, 1, 0) plane-local up axis and
 *  returns the Y component of the result — the world-space Y of the
 *  surface normal. Standard quaternion-vector rotation formula
 *  expanded and simplified for the (0,1,0) input. Pure — testable. */
export function surfaceNormalWorldY(q: {
  x: number
  y: number
  z: number
  w: number
}): number {
  // For unit quaternion, result.y of q · (0,1,0) · q⁻¹ =
  //   1 - 2*(x² + z²)
  return 1 - 2 * (q.x * q.x + q.z * q.z)
}

interface Vec3 {
  x: number
  y: number
  z: number
}

/** Applies a quaternion to the plane-local (0, 1, 0) up axis and
 *  returns the world-space normal as a full 3-vector. Standard
 *  q·v·q⁻¹ formula expanded for v = (0, 1, 0). Pure — testable. */
export function surfaceNormalWorld(q: {
  x: number
  y: number
  z: number
  w: number
}): Vec3 {
  return {
    x: 2 * (q.x * q.y - q.w * q.z),
    y: 1 - 2 * (q.x * q.x + q.z * q.z),
    z: 2 * (q.y * q.z + q.w * q.x),
  }
}

/** Projects world +Y onto a plane defined by its normal, then
 *  normalises — the result is the "gravity-up" direction ON the plane
 *  (which line runs vertically as seen by someone standing upright).
 *  Used to orient the anchored wall grid so its lines read as
 *  horizontal + vertical relative to the real-world floor.
 *
 *  Fallback: when the plane's normal is aligned with +Y (a floor or
 *  ceiling), the projection is zero and there's no meaningful "up on
 *  the plane" direction. Returns +X as a sensible tangent. Pure. */
export function computeGravityUpOnPlane(normal: Vec3): Vec3 {
  // proj = worldY - (worldY · normal) * normal
  // (worldY · normal) is just normal.y for worldY = (0, 1, 0)
  const dot = normal.y
  const projX = 0 - dot * normal.x
  const projY = 1 - dot * normal.y
  const projZ = 0 - dot * normal.z
  const len = Math.sqrt(projX * projX + projY * projY + projZ * projZ)
  if (len < 0.001) {
    // Horizontal plane — pick +X (perpendicular to +Y, on any horizontal plane).
    return { x: 1, y: 0, z: 0 }
  }
  return { x: projX / len, y: projY / len, z: projZ / len }
}

/** Cosine of the re-anchor threshold. 30° tolerance means the same
 *  wall stays anchored even when ARCore's per-hit normal wobbles by
 *  ~20°; a 60°+ divergence triggers a fresh anchor for a different
 *  wall. cos(30°) ≈ 0.866. */
export const REANCHOR_THRESHOLD_COS = 0.866

/** Anchoring gate: keep the grid stationary as long as the current
 *  hit's normal is roughly the same direction as the anchor's. Pure. */
export function shouldReanchorWallGrid(
  currentNormal: Vec3,
  anchorNormal: Vec3 | null,
): boolean {
  if (anchorNormal == null) return true
  const dot =
    currentNormal.x * anchorNormal.x +
    currentNormal.y * anchorNormal.y +
    currentNormal.z * anchorNormal.z
  return dot < REANCHOR_THRESHOLD_COS
}

/** World-space frame that anchors a gravity-aligned grid to a wall.
 *  Set once when the hit lands on a wall; held stationary until the
 *  user pans to a genuinely different wall (dot < REANCHOR_THRESHOLD_COS).
 *  Cleared to null on session end. */
export interface AnchoredPlaneInfo {
  readonly position: Vec3
  readonly normal: Vec3
  readonly gravityUp: Vec3
  readonly gravityRight: Vec3
}

/** Diagnostic snapshot answering "did we actually get plane detection
 *  and what surface API does this Chrome/ARCore expose?" — the ground
 *  truth for triaging why plane detection may be returning 0. */
export interface WebXRSessionInfo {
  /** From session.enabledFeatures (WebXR spec-required field). Contains
   *  every feature Chrome ACTUALLY granted at session start — regardless
   *  of what we requested. Absence of 'plane-detection' here means the
   *  device / Chrome / Play Services combo doesn't support it. */
  readonly enabledFeatures: string[]
  /** Runtime type of frame.detectedPlanes on the most recent frame.
   *  'undefined' → API not implemented; 'Set(0)' → API works but
   *  ARCore has found nothing; 'Set(N)' → we're getting planes. */
  readonly detectedPlanesType: string
  /** Truncated User-Agent — Chrome version + Android version live here. */
  readonly userAgent: string
  /** Current classification of the surface the camera-forward ray hits.
   *  Derived per-frame from the hit-test result's pose orientation.
   *  'none' before the first hit lands. */
  readonly currentSurface: SurfaceClass
  /** Raw Y component of the current surface normal — shown in the HUD
   *  so we can see the underlying signal + tune thresholds by eye. */
  readonly currentSurfaceNormalY: number
  /** Current anchored wall frame — null before the first wall hit AND
   *  after the session ends. Same reference is held across frames as
   *  long as the user's aim stays on roughly the same wall (dot with
   *  anchor normal > REANCHOR_THRESHOLD_COS); replaced with a fresh
   *  frame when they pan to a genuinely different wall. Non-wall
   *  frames DO NOT clear this — the grid stays put until the next
   *  wall hit. */
  readonly currentAnchoredWall: AnchoredPlaneInfo | null
  /** Tracking-convergence signals — snapshot each frame. Consumers
   *  feed these to computeTrackingConvergence() in
   *  @/ar/tracking-convergence to render the coaching progress HUD. */
  readonly elapsedMs: number
  readonly viewerPoseFrames: number
  readonly hitTestFramesWithResult: number
  readonly hitTestFramesEmpty: number
  /** Derived from the viewer pose each frame: true when the phone is
   *  physically rolled to landscape (right or left). WebXR immersive-
   *  ar suppresses screen.orientation / resize / matchMedia events, so
   *  the DOM has no other way to know the phone was rotated during a
   *  session. UI polls this via getSessionInfo(). */
  readonly viewerIsLandscape: boolean
  /** Physical roll of the phone, snapped to a quarter turn. Same
   *  convention as `screen.orientation.angle`; see
   *  deriveViewerRollQuadrant. */
  readonly viewerRollQuadrant: 0 | 90 | 180 | 270
  /** Anchor-subsystem diagnostics. Distinguishes "wheel teleport
   *  because the 'anchors' feature wasn't granted" (created > 0 &&
   *  landed === 0) from "wheel teleport despite working anchors"
   *  (landed > 0 && anchor pose is moving). Displayed in the HUD. */
  readonly anchorStatus: AnchorStatus
  /** Depth-sensing diagnostics — see DepthStatus. Stays at
   *  DEPTH_STATUS_INITIAL when depth sensing wasn't requested. */
  readonly depthStatus: DepthStatus
}

/** Depth-sensing diagnostics — the on-device measurement surface for
 *  the Z-plane precision work. lastDeltaM is THE number: how far
 *  behind the depth-sensed surface the plane hit-test would have
 *  placed the wheel (positive = plane hit is behind the real surface,
 *  i.e. the car-in-front-of-wall case). */
export interface DepthStatus {
  /** Was depth sensing requested at session start (?depthHud /
   *  ?depthPlace on the URL)? */
  readonly requested: boolean
  /** Did session.enabledFeatures contain 'depth-sensing'? False on
   *  devices whose ARCore build has no Depth API. */
  readonly featureGranted: boolean
  /** Frames where getDepthInformation returned a usable center sample. */
  readonly framesWithDepth: number
  /** Frames where depth was requested but no usable sample arrived. */
  readonly framesWithoutDepth: number
  /** Last usable center-pixel depth, meters. Held (not cleared) across
   *  sample gaps so the HUD stays readable; freshness is visible via
   *  the frame counters. */
  readonly lastDepthM: number | null
  /** Camera distance of the plane hit-test result on the same frame as
   *  the last depth sample. */
  readonly lastPlaneHitDistanceM: number | null
  /** lastPlaneHitDistanceM − lastDepthM. Positive ≈ the plane hit is
   *  behind the depth-sensed surface. */
  readonly lastDeltaM: number | null
  /** session.depthUsage as granted by the runtime ('cpu-optimized' /
   *  'gpu-optimized'). gpu-optimized would explain zero CPU samples —
   *  getDepthInMeters only exists on the CPU path. */
  readonly depthUsage: string | null
  /** session.depthDataFormat as granted ('luminance-alpha' / 'float32'). */
  readonly depthDataFormat: string | null
  /** Why the most recent sample attempt produced nothing — the 14T Pro
   *  triage signal. null after a successful sample. */
  readonly lastFailure: string | null
}

export const DEPTH_STATUS_INITIAL: DepthStatus = {
  requested: false,
  featureGranted: false,
  framesWithDepth: 0,
  framesWithoutDepth: 0,
  lastDepthM: null,
  lastPlaneHitDistanceM: null,
  lastDeltaM: null,
  depthUsage: null,
  depthDataFormat: null,
  lastFailure: null,
}

export interface AnchorStatus {
  /** Did session.enabledFeatures contain 'anchors' at start? If false,
   *  frame.createAnchor calls will silently reject — no anchor will
   *  ever land, and wheels use the pre-anchor stored-coord path. */
  readonly featureGranted: boolean
  /** Cumulative count of driver.createAnchor calls (== wheels placed
   *  when the driver supports anchors). Compare to `landed` to spot
   *  "created without landing" — the reject-silently failure mode. */
  readonly created: number
  /** Cumulative count of async frame.createAnchor resolves. */
  readonly landed: number
  /** Cumulative count of async frame.createAnchor rejects. */
  readonly rejected: number
  /** Cumulative count of frames where an anchor's pose returned null
   *  (tracking lost). Useful signal for how "solid" ARCore's anchor
   *  tracking is on this device. */
  readonly poseLostFrames: number
  /** For the FIRST placed wheel: cumulative displacement of its anchor
   *  from its initial pose. If this grows without bound after the wheel
   *  is planted, ARCore is drifting the anchor itself (failure mode B).
   *  Undefined until a wheel is placed. */
  readonly firstAnchorInitialPos: { x: number; y: number; z: number } | null
  readonly firstAnchorCurrentPos: { x: number; y: number; z: number } | null
  readonly firstAnchorDriftMeters: number
}

/**
 * The world position at which we place wheels — camera-forward × PREVIEW_DISTANCE_M.
 * Mathematically equivalent to camera.localToWorld((0, 0, -PREVIEW_DISTANCE_M)),
 * which is the preview wheel's world position at the moment this is called.
 *
 * Why this shape: the preview wheel is a child of the camera at fixed
 * camera-local offset (0, 0, -PREVIEW_DISTANCE_M). At the moment the user
 * taps, we want the placed (world-anchored) wheel to appear at the EXACT
 * SAME visible screen location as the preview. That requires the placed
 * wheel to be at the same distance from the camera as the preview — else
 * perspective projection makes them appear at different sizes even though
 * their matrix scales are equal.
 *
 * Pure function — takes an updated camera, returns the placement world
 * position. Testable without WebXR runtime. Any change to the preview's
 * camera-local offset must be mirrored here or the visible-size
 * invariant breaks.
 */
export function computePlacementWorldPosition(
  camera: THREE.Camera,
): { x: number; y: number; z: number } {
  const local = new THREE.Vector3(0, 0, -PREVIEW_DISTANCE_M)
  camera.localToWorld(local)
  return { x: local.x, y: local.y, z: local.z }
}

interface NavigatorWithXR {
  xr?: XRSystem
}

export class WebXRDriver implements ARDriver {
  /** ?depthHud / ?depthPlace — request the 'depth-sensing' feature at
   *  session start and sample the center-pixel depth each frame. Off
   *  by default: requesting depth costs ARCore CPU per frame even when
   *  nothing reads it. */
  private readonly enableDepthSensing: boolean
  /** ?surfaceYaw — also sample two off-center depth pixels per read so
   *  placement can derive the struck surface's yaw. Costs two extra
   *  CPU pixel reads on the existing sampling cadence, nothing more. */
  private readonly sampleDepthNeighborhood: boolean

  /** Fired once when the XR session ends, BEFORE any teardown, so a
   *  listener still sees the session's final counters. Used for the
   *  session-summary telemetry; the driver deliberately does not emit
   *  that itself, because the summary also needs renderer and
   *  controller state the driver has no business reaching for. */
  private readonly onSessionEnded: (() => void) | null

  constructor(
    opts: {
      enableDepthSensing?: boolean
      sampleDepthNeighborhood?: boolean
      onSessionEnded?: () => void
    } = {},
  ) {
    this.enableDepthSensing = opts.enableDepthSensing ?? false
    this.sampleDepthNeighborhood = opts.sampleDepthNeighborhood ?? false
    this.onSessionEnded = opts.onSessionEnded ?? null
    // Reflect the request in the HUD from the first paint — the session
    // (and with it featureGranted) only exists after Start AR.
    this.sessionInfo = {
      ...this.sessionInfo,
      depthStatus: {
        ...DEPTH_STATUS_INITIAL,
        requested: this.enableDepthSensing,
      },
    }
  }

  private started = false
  private session: XRSession | null = null

  /** Queued screenshot, run on the NEXT XR frame — see requestCapture. */
  private pendingCapture:
    | ((context: {
        session: XRSession | null
        frame: XRFrame | null
        referenceSpace: XRReferenceSpace | null
        renderer: THREE.WebGLRenderer | null
      }) => void)
    | null = null

  /**
   * Runs `run` inside the next XR animation frame.
   *
   * Raw camera access is frame-scoped: the texture from
   * getCameraImage() is only valid during the requestAnimationFrame
   * callback of the frame that produced it. Calling it from a click
   * handler with a stashed frame returns nothing, which is exactly why
   * the first camera button reported "couldn't save" every time
   * (2026-08-23 LUAT). Deferring to the frame loop is not an
   * optimisation here — it is the only legal place to ask.
   */
  requestCapture(
    run: (context: {
      session: XRSession | null
      frame: XRFrame | null
      referenceSpace: XRReferenceSpace | null
      renderer: THREE.WebGLRenderer | null
    }) => void,
  ): void {
    this.pendingCapture = run
  }
  private webglRenderer: THREE.WebGLRenderer | null = null
  private threeCamera: THREE.PerspectiveCamera | null = null
  private threeScene: THREE.Scene | null = null
  private referenceSpace: XRReferenceSpace | null = null
  private hitTestSource: XRHitTestSource | null = null
  private cachedHit: HitResult | null = null
  /** Secondary hit-test source aimed ~9° below the viewer-forward ray so
   *  it targets the ground when the operator points the phone roughly
   *  ahead of them. Used by the ?groundLine feature to derive a fixed
   *  Y coordinate for the ground plane from the first tap. */
  private groundHitTestSource: XRHitTestSource | null = null
  private cachedGroundHit: HitResult | null = null
  /** Latest usable depth-map center sample as a world-space hit.
   *  Cleared on any SAMPLED frame without a usable result — a stale
   *  depth point is a wrong placement. Skipped (throttled) frames keep
   *  the previous sample: ≤50 ms of staleness at 20 Hz sampling. */
  private cachedDepthHit: HitResult | null = null
  /** ?surfaceYaw — latest neighbourhood chord (world space), refreshed
   *  with the center sample; null whenever either side was unusable. */
  private cachedDepthChord: {
    left: { x: number; y: number; z: number }
    right: { x: number; y: number; z: number }
  } | null = null
  /** ?surfaceYaw — the REAL pose-derived world normal of cachedHit.
   *  Kept separate because cachedHit.normal is a deliberate placeholder
   *  (see the free-tracking comment where both are written): consumers
   *  of the placeholder — isHorizontalSurface among them — must not
   *  change behaviour underneath the flag. */
  private cachedHitSurfaceNormal: { x: number; y: number; z: number } | null =
    null
  /** Monotonic frame counter driving the depth sampling throttle. */
  private depthFrameIndex = 0
  /** 1 Hz DevTools diagnostics cadence — perf.now of the last emit and
   *  the frame count at that moment (for the fps estimate). */
  private lastDiagLogMs = 0
  private lastDiagFrameCount = 0
  private diagFrameCount = 0
  private hasFirstHit = false
  private onSessionState: ((state: ARSessionState) => void) | null = null
  private onUpdateCb: (() => void) | null = null

  // Detected planes tracking. Keyed by XRPlane object reference — those
  // are stable across frames per the WebXR plane-detection spec. Snapshot
  // rebuilt into detectedPlanesSnapshot each frame for consumers.
  private planeIdCounter = 0
  private planeIdByRef = new WeakMap<XRPlane, string>()
  private detectedPlanesSnapshot: DetectedPlaneInfo[] = []
  private planeListeners = new Set<() => void>()

  // Anchor tracking. createAnchor mints an id synchronously and kicks
  // off the async XRAnchor request; when the anchor resolves we store
  // it under that id. getAnchorPosition samples the anchor's current
  // world pose against the latest XRFrame each call. Resolves the
  // "wheel teleports on erratic phone motion" issue: ARCore keeps the
  // anchor's world pose consistent across re-localizations, so the
  // wheel's world coord follows the same real-world point.
  private lastFrame: XRFrame | null = null
  private anchorIdCounter = 0
  private anchors = new Map<string, XRAnchor>()
  /** createAnchor requests queued from user taps (which run OUTSIDE
   *  the XR frame callback → frame.createAnchor throws
   *  InvalidStateError if called with a stale XRFrame). Drained
   *  synchronously at the top of onFrame where the live frame is
   *  guaranteed valid. Two shapes:
   *   - kind 'world': manufactured-pose world anchor (fallback path).
   *   - kind 'plane': uses the current frame's XRHitTestResult so the
   *     anchor is plane-attached (much more stable — ARCore tracks
   *     the underlying plane and updates the anchor with it). */
  private pendingAnchorRequests: Array<
    | { kind: 'world'; id: string; hit: HitResult }
    | { kind: 'plane'; id: string }
  > = []

  /** Session-scoped diagnostic snapshot for the HUD. Updated when the
   *  session starts (enabledFeatures) and on the first frame (detected
   *  planes type). Read directly by the HUD; listeners fire via
   *  notifyPlaneListeners so React re-renders pick up changes. */
  sessionInfo: WebXRSessionInfo = {
    enabledFeatures: [],
    detectedPlanesType: '(no frame yet)',
    userAgent:
      typeof navigator === 'undefined' ? '' : navigator.userAgent || '',
    currentSurface: 'none',
    currentSurfaceNormalY: 0,
    currentAnchoredWall: null,
    elapsedMs: 0,
    viewerPoseFrames: 0,
    hitTestFramesWithResult: 0,
    hitTestFramesEmpty: 0,
    viewerIsLandscape: false,
    viewerRollQuadrant: 0,
    anchorStatus: {
      featureGranted: false,
      created: 0,
      landed: 0,
      rejected: 0,
      poseLostFrames: 0,
      firstAnchorInitialPos: null,
      firstAnchorCurrentPos: null,
      firstAnchorDriftMeters: 0,
    },
    depthStatus: DEPTH_STATUS_INITIAL,
  }

  private firstAnchorId: string | null = null

  /** performance.now() at session start; used to compute
   *  sessionInfo.elapsedMs each frame for tracking-convergence HUD. */
  private sessionStartPerfMs = 0

  /** Store of currently-detected ARCore planes. React HUDs subscribe
   *  via useSyncExternalStore; the wheel renderer polls in tick(). */
  readonly detectedPlanes: DetectedPlanesStore = {
    getState: () => this.detectedPlanesSnapshot,
    subscribe: (listener) => {
      this.planeListeners.add(listener)
      return () => {
        this.planeListeners.delete(listener)
      }
    },
  }

  async start(opts: {
    canvas?: HTMLCanvasElement
    threeScene: THREE.Scene
    threeCamera: THREE.PerspectiveCamera
    onSessionState: (state: ARSessionState) => void
    onSceneReady?: (xrScene: {
      scene: THREE.Scene
      camera: THREE.PerspectiveCamera
    }) => void
    onUpdate?: () => void
    onRendererReady?: (renderer: THREE.WebGLRenderer) => void
  }): Promise<void> {
    if (this.started) return
    if (opts.canvas == null) {
      throw new Error('WebXRDriver requires a canvas')
    }

    const xr = (navigator as NavigatorWithXR).xr
    if (xr == null) {
      // First gate: the browser doesn't expose the WebXR API at all.
      // Firefox / Safari mobile / some Chromium forks. Switch to Chrome.
      throw new Error(
        'navigator.xr is unavailable — this browser does not implement WebXR. Try Chrome on Android.',
      )
    }
    const supported = await xr.isSessionSupported('immersive-ar')
    if (!supported) {
      // Second gate: WebXR is exposed but the device says immersive-ar
      // is not available. The most common cause on Samsung / non-Pixel
      // Android is that "Google Play Services for AR" (ARCore's
      // device-side service) isn't installed. Install it from:
      // https://play.google.com/store/apps/details?id=com.google.ar.core
      // and hard-reload. Less common: the device isn't on ARCore's
      // supported list (see https://developers.google.com/ar/devices).
      throw new Error(
        'immersive-ar not available. Most likely fix: install "Google Play Services for AR" from the Play Store (com.google.ar.core), then reload. Confirm device is on the ARCore supported list.',
      )
    }

    this.onSessionState = opts.onSessionState
    this.onUpdateCb = opts.onUpdate ?? null
    this.threeCamera = opts.threeCamera
    this.threeScene = opts.threeScene

    this.onSessionState(arSessionState.initializing())

    // The WebGLRenderer owns the canvas + GL context. Three.js's
    // WebXRManager mutates this camera's pose every XR frame from the
    // session's viewer pose; we render the scene against it.
    const renderer = new THREE.WebGLRenderer({
      canvas: opts.canvas,
      alpha: true,
      antialias: true,
    })
    renderer.setPixelRatio(window.devicePixelRatio)
    renderer.setSize(window.innerWidth, window.innerHeight, false)
    renderer.xr.enabled = true
    this.webglRenderer = renderer

    // dom-overlay lets our React tree (StartAR button, coaching overlay,
    // diagnostics HUD, top controls) render ON TOP of the AR camera feed.
    // Without it, immersive-ar mode hides the entire DOM. hit-test is
    // required for surface placement.
    //
    // 'local' must be declared as a required feature — recent Chrome +
    // ARCore versions don't auto-allocate it. Without this declaration,
    // session.requestReferenceSpace('local') throws
    // "This device does not support the requested reference space type"
    // on the Galaxy A36 and other devices. 'local-floor' is nice-to-have
    // for floor-anchored placement; degrades gracefully to 'local' if
    // the runtime can't establish a floor plane.
    const optionalFeatures = [
      'dom-overlay',
      'local-floor',
      'plane-detection',
      // 'anchors' — required for placed-wheel persistence across ARCore
      // re-localization. Optional so devices without anchor support
      // still get a session (renderer degrades to the pre-anchor
      // stored-coord behaviour).
      'anchors',
      // 'light-estimation' — ARCore camera-derived lighting (ambient
      // spherical-harmonic probe, directional key light, and on capable
      // devices a reflection cube map). Consumed by XREstimatedLight in
      // SceneLighting so the wheel matches real-world brightness instead
      // of rendering dark. Optional: devices without it fall back to the
      // neutral RoomEnvironment baseline.
      'light-estimation',
      // 'camera-access' — WebXR Raw Camera Access. The ONLY way a page
      // can obtain the passthrough image: in immersive-ar the camera
      // feed is composited by the system, so our canvas holds just the
      // wheel on transparency and a canvas capture would save exactly
      // that. Optional — without it the camera button reports that
      // capture is unavailable rather than saving a wrong picture.
      'camera-access',
    ]
    const sessionInit: XRSessionInit & {
      depthSensing?: {
        usagePreference: string[]
        dataFormatPreference: string[]
      }
    } = {
      requiredFeatures: ['hit-test', 'local'],
      optionalFeatures,
      domOverlay: { root: document.body },
    }
    if (this.enableDepthSensing) {
      // 'depth-sensing' — the ARCore Depth API. cpu-optimized because we
      // read single pixels on the CPU (getDepthInMeters), not a GPU
      // texture. luminance-alpha (uint16 mm) is the format every ARCore
      // depth device supports; float32 is the fallback preference.
      // Optional feature: devices without the Depth API still get a
      // session and DepthStatus.featureGranted stays false.
      optionalFeatures.push('depth-sensing')
      sessionInit.depthSensing = {
        usagePreference: ['cpu-optimized'],
        dataFormatPreference: ['luminance-alpha', 'float32'],
      }
    }
    const session = await xr.requestSession(
      'immersive-ar',
      sessionInit as XRSessionInit,
    )
    this.session = session

    // Snapshot which features Chrome+ARCore ACTUALLY granted. If
    // 'plane-detection' is missing here, the device/browser combo
    // doesn't support it and we know to pivot away from the feature.
    // `enabledFeatures` is spec-defined but may be missing on older
    // Chrome versions — hence the optional-property access.
    const rawEnabledFeatures = (
      session as unknown as { enabledFeatures?: unknown }
    ).enabledFeatures
    const enabledFeatures = Array.from(
      (rawEnabledFeatures as Iterable<string> | undefined) ?? [],
    )
    // eslint-disable-next-line no-console
    console.log('[webxr-driver] session started. raw enabledFeatures:', rawEnabledFeatures, 'parsed:', enabledFeatures, 'session keys:', Object.keys(session as object))
    this.sessionInfo = {
      ...this.sessionInfo,
      enabledFeatures,
      anchorStatus: {
        ...this.sessionInfo.anchorStatus,
        featureGranted: enabledFeatures.includes('anchors'),
      },
      depthStatus: {
        ...this.sessionInfo.depthStatus,
        requested: this.enableDepthSensing,
        featureGranted: enabledFeatures.includes('depth-sensing'),
        // What the runtime ACTUALLY granted. gpu-optimized usage would
        // make CPU getDepthInMeters unavailable → 0 usable samples.
        ...readGrantedDepthConfig(session, enabledFeatures),
      },
    }
    // Force a new snapshot reference so useSyncExternalStore actually
    // re-renders on this sessionInfo change.
    this.detectedPlanesSnapshot = [...this.detectedPlanesSnapshot]
    this.notifyPlaneListeners()

    // 'end' fires when the user backs out of XR (system button, browser
    // close, etc). Treat as a clean shutdown.
    session.addEventListener('end', () => {
      // First, while the counters are still intact.
      try {
        this.onSessionEnded?.()
      } catch (err) {
        // A telemetry listener must never block session teardown.
        console.warn('[webxr-driver] onSessionEnded listener threw', err)
      }
      this.session = null
      this.hitTestSource = null
      this.groundHitTestSource = null
      this.cachedGroundHit = null
      this.cachedDepthHit = null
      this.referenceSpace = null
      this.sessionInfo = {
        ...this.sessionInfo,
        viewerIsLandscape: false,
        viewerRollQuadrant: 0,
      }
      this.started = false
      this.hasFirstHit = false
      this.cachedHit = null
      this.cachedHitSurfaceNormal = null
      this.cachedDepthChord = null
      // XRAnchor.delete throws once the session has ended; just drop
      // our references and let the runtime GC clean up.
      this.anchors.clear()
      this.pendingAnchorRequests = []
      this.firstAnchorId = null
      this.lastFrame = null
      this.sessionInfo = {
        ...this.sessionInfo,
        currentSurface: 'none',
        currentSurfaceNormalY: 0,
        currentAnchoredWall: null,
        anchorStatus: {
          featureGranted: this.sessionInfo.anchorStatus.featureGranted,
          created: 0,
          landed: 0,
          rejected: 0,
          poseLostFrames: 0,
          firstAnchorInitialPos: null,
          firstAnchorCurrentPos: null,
          firstAnchorDriftMeters: 0,
        },
        depthStatus: {
          ...DEPTH_STATUS_INITIAL,
          requested: this.sessionInfo.depthStatus.requested,
          featureGranted: this.sessionInfo.depthStatus.featureGranted,
        },
      }
    })

    // Hand the WebGLRenderer to the wheel renderer BEFORE setSession so it
    // can construct XREstimatedLight, which must subscribe to the renderer's
    // `sessionstart` event that setSession fires next.
    opts.onRendererReady?.(renderer)

    // Classic XRWebGLLayer, not WebXR Layers — see with-classic-xr-layer.ts.
    await withClassicXrLayer(() => renderer.xr.setSession(session))

    // Prefer 'local-floor' (floor plane origin, correct wheel height),
    // fall back to 'local' (session-start pose origin). Both cases yield
    // a world-anchored reference; only Y is different, which the scene
    // graph absorbs via the wheel's placement transform. 'viewer' is
    // the camera-relative space used as the hit-test source so we
    // always raycast along the camera's forward axis.
    try {
      this.referenceSpace = await session.requestReferenceSpace('local-floor')
    } catch {
      this.referenceSpace = await session.requestReferenceSpace('local')
    }
    const viewerRef = await session.requestReferenceSpace('viewer')
    this.hitTestSource = await session.requestHitTestSource!({
      space: viewerRef,
    }) ?? null

    // Downward-angled hit-test source for the ?groundLine feature — a
    // ray that leaves the viewer heading ~9° below straight forward
    // (matches the blue reference line at screen Y ≈ 65 % on a ~60°
    // vertical FOV phone camera). Feature-flag consumers use
    // getGroundHit() to raycast for the ground Y coordinate.
    const XRRayCtor = (globalThis as unknown as {
      XRRay?: new (
        origin: DOMPointInit,
        direction: DOMPointInit,
      ) => XRRay
    }).XRRay
    if (XRRayCtor != null) {
      const groundRay = new XRRayCtor(
        { x: 0, y: 0, z: 0, w: 1 },
        { x: 0, y: -0.156, z: -0.988, w: 0 },
      )
      try {
        this.groundHitTestSource = await session.requestHitTestSource!({
          space: viewerRef,
          offsetRay: groundRay,
        }) ?? null
      } catch (err) {
        // Without this source there is no floor estimate and therefore
        // never a ground line — half of the "no green line" symptom.
        atoLog.error('ground_hit_test_source', err)
        // eslint-disable-next-line no-console
        console.warn('[webxr-driver] groundHitTestSource request failed', err)
      }
    }

    this.onSessionState(arSessionState.scanning())
    this.started = true
    this.sessionStartPerfMs = performance.now()
    this.lastDiagLogMs = 0
    this.diagFrameCount = 0
    this.lastDiagFrameCount = 0
    this.depthFrameIndex = 0

    // The XR animation loop runs at the display's native frame rate
    // (typically 60Hz on Android) and provides an XRFrame from which
    // we can sample pose + hit-test results.
    renderer.setAnimationLoop((_time: number, frame: XRFrame | null) => {
      this.onFrame(frame)
    })
  }

  private onFrame(frame: XRFrame | null): void {
    if (frame == null) return
    if (this.referenceSpace == null) return
    if (this.threeCamera == null) return

    // Stash for anchor pose sampling — getAnchorPosition is called from
    // the renderer's tick() (which runs inside onUpdateCb below), so it
    // needs the current frame to resolve anchor.anchorSpace to a pose.
    this.lastFrame = frame

    // Screenshot requested since the last frame? This is the only point
    // where the camera image is obtainable — see requestCapture.
    if (this.pendingCapture != null) {
      const run = this.pendingCapture
      this.pendingCapture = null
      run({
        session: this.session,
        frame,
        referenceSpace: this.referenceSpace,
        renderer: this.webglRenderer,
      })
    }

    // Tracking-convergence signals — for the coaching progress HUD.
    // Viewer-pose availability = "SLAM is up"; elapsed time is used
    // for the warmup ramp + place-anyway unlock timer.
    const viewerPose = frame.getViewerPose(this.referenceSpace)
    const viewerPoseFrames =
      this.sessionInfo.viewerPoseFrames + (viewerPose != null ? 1 : 0)

    // Physical device orientation, derived from the viewer pose. WebXR
    // immersive-ar swallows all DOM orientation events on Chrome
    // Android, so this is the ONLY reliable signal while a session is
    // active. Math: project world +Y (gravity-up) onto the camera's
    // local +Y (screen-top) and +X (screen-right) axes; whichever's
    // absolute value dominates tells us the phone's roll — irrespective
    // of pitch or yaw. See `deriveViewerIsLandscape` at file bottom.
    let viewerRollQuadrant = this.sessionInfo.viewerRollQuadrant
    if (viewerPose != null) {
      viewerRollQuadrant = deriveViewerRollQuadrant(
        viewerPose.transform.orientation,
      )
    }
    const viewerIsLandscape =
      viewerRollQuadrant === 90 || viewerRollQuadrant === 270

    // Drain pending anchor-create requests queued from user taps
    // (which fire OUTSIDE the XR frame callback and therefore cannot
    // call frame.createAnchor directly — throws InvalidStateError).
    // Must happen before hit-test / anchor sampling so a fresh anchor
    // can be pinned same-frame.
    this.flushPendingAnchorRequests(frame)

    // ARCore hit-test — targets REAL detected planes (walls, floors, etc.).
    // Every frame we sample the intersection of the camera-forward ray
    // with the closest detected plane; the preview snaps to that world
    // point. As ARCore identifies more planes over time (or larger ones
    // as it walks a texture), subsequent hits use the newly-preferred
    // surface automatically.
    let hitTestFramesWithResult = this.sessionInfo.hitTestFramesWithResult
    let hitTestFramesEmpty = this.sessionInfo.hitTestFramesEmpty
    if (this.hitTestSource != null) {
      const hits = frame.getHitTestResults(this.hitTestSource)
      if (hits.length > 0) {
        hitTestFramesWithResult += 1
        const pose = hits[0].getPose(this.referenceSpace)
        if (pose != null) {
          // Classify FIRST, then decide whether to refresh cachedHit.
          // Non-wall hits (floors / ceilings / skew) don't move the
          // wheel — it holds its last wall position, like a mouse
          // cursor whose last-known screen position is held when it
          // leaves the window. HUD still updates so the developer
          // sees the current classification, even when the wheel is
          // locked.
          const normal = surfaceNormalWorld(pose.transform.orientation)
          const normalY = normal.y
          const surface = classifySurface(normalY)

          // Wall-only anchoring: build a new AnchoredPlaneInfo only when
          // the hit is on a wall AND the current normal diverges enough
          // from the anchor's (or no anchor yet). Non-wall hits leave
          // the existing anchor intact — user's grid persists across a
          // brief pan over a floor / shadow / off-plane region.
          let nextAnchor = this.sessionInfo.currentAnchoredWall
          if (
            surface === 'wall' &&
            shouldReanchorWallGrid(normal, nextAnchor?.normal ?? null)
          ) {
            const gravityUp = computeGravityUpOnPlane(normal)
            const gravityRight = crossVec3(gravityUp, normal)
            const t = pose.transform.position
            nextAnchor = {
              position: { x: t.x, y: t.y, z: t.z },
              normal,
              gravityUp,
              gravityRight,
            }
          }

          if (
            surface !== this.sessionInfo.currentSurface ||
            Math.abs(normalY - this.sessionInfo.currentSurfaceNormalY) > 0.02 ||
            nextAnchor !== this.sessionInfo.currentAnchoredWall
          ) {
            this.sessionInfo = {
              ...this.sessionInfo,
              currentSurface: surface,
              currentSurfaceNormalY: normalY,
              currentAnchoredWall: nextAnchor,
            }
            this.detectedPlanesSnapshot = [...this.detectedPlanesSnapshot]
            this.notifyPlaneListeners()
          }

          // Free-tracking: refresh cachedHit on ANY valid hit, regardless
          // of surface classification. Users found the wall-only gate
          // false-positived on shadows (classifier read the shadow as a
          // horizontal surface and froze the wheel). Classification is
          // still computed + shown on the HUD so the developer can see
          // what ARCore reports, but placement is user-initiated and
          // never gated on wall detection. shouldTrackHit is still
          // exported (and tested) in case we revisit a wall-only mode.
          const t = pose.transform.position
          this.cachedHit = {
            position: { x: t.x, y: t.y, z: t.z },
            // Identity-orientation normal — placed wheel keeps default
            // GLB orientation regardless of the plane's real normal.
            // Prevents "wheel lying flat facing ceiling" if ARCore
            // reports a horizontal plane like a floor.
            normal: { x: 0, y: 0, z: 1 },
          }
          // The REAL normal rides in a separate slot for ?surfaceYaw —
          // swapping it into cachedHit.normal would change every
          // placeholder consumer (isHorizontalSurface) unflagged.
          this.cachedHitSurfaceNormal = normal
        }
      } else {
        hitTestFramesEmpty += 1
      }
      // If ARCore hasn't detected a plane yet OR the camera is aimed
      // away from any known plane, we keep the previous cachedHit so
      // the preview stays put rather than snapping to origin.
    }

    // Ground hit-test — samples the angled-down ray so getGroundHit()
    // can freeze a ground Y coordinate on the first tap. Only present
    // when the browser exposes XRRay + offsetRay support.
    if (this.groundHitTestSource != null) {
      const groundHits = frame.getHitTestResults(this.groundHitTestSource)
      if (groundHits.length > 0) {
        const gPose = groundHits[0].getPose(this.referenceSpace)
        if (gPose != null) {
          const t = gPose.transform.position
          this.cachedGroundHit = {
            position: { x: t.x, y: t.y, z: t.z },
            normal: { x: 0, y: 1, z: 0 },
          }
        }
      } else {
        this.cachedGroundHit = null
      }
    }

    // Depth-map center sample (?depthHud / ?depthPlace). Runs after the
    // plane hit-test block so the same-frame plane distance is available
    // for the delta diagnostic. Updates cachedDepthHit + depthStatus;
    // folded into the single per-frame sessionInfo write below.
    //
    // GATED (2026-08-20 UAT): never during SLAM warm-up and then only
    // every Nth frame — per-frame cpu-optimized reads starved ARCore's
    // feature tracking on the 14T Pro and coaching never completed.
    // Skipped frames leave cachedDepthHit + counters untouched.
    this.depthFrameIndex += 1
    const depthStatus = shouldSampleDepth({
      frameIndex: this.depthFrameIndex,
      trackingReady: this.hasFirstHit,
    })
      ? this.sampleCenterDepth(frame, viewerPose)
      : this.sessionInfo.depthStatus

    // Push tracking-convergence counters onto sessionInfo. Bumped in a
    // single write per frame so React consumers see one coherent
    // update, not four.
    this.sessionInfo = {
      ...this.sessionInfo,
      elapsedMs: performance.now() - this.sessionStartPerfMs,
      viewerPoseFrames,
      hitTestFramesWithResult,
      hitTestFramesEmpty,
      viewerIsLandscape,
      viewerRollQuadrant,
      depthStatus,
    }
    this.detectedPlanesSnapshot = [...this.detectedPlanesSnapshot]
    this.notifyPlaneListeners()

    // Sample all detected ARCore planes for HUD + visualisations.
    this.updateDetectedPlanes(frame)

    // 1 Hz DevTools diagnostics (chrome://inspect → filter [webxr-diag]).
    // Always on: one line per second is harmless, and the flags-vs-no-flags
    // A/B needs the line present in BOTH configurations.
    this.diagFrameCount += 1
    const nowMs = performance.now()
    if (nowMs - this.lastDiagLogMs >= 1000) {
      const fps =
        this.lastDiagLogMs === 0
          ? 0
          : ((this.diagFrameCount - this.lastDiagFrameCount) * 1000) /
            (nowMs - this.lastDiagLogMs)
      // eslint-disable-next-line no-console
      console.log(formatWebXrDiagLine(this.sessionInfo, fps))
      this.lastDiagLogMs = nowMs
      this.lastDiagFrameCount = this.diagFrameCount
    }

    // Anchor telemetry — track the first placed wheel's anchor drift
    // so the HUD can distinguish "anchor never landed" from "anchor
    // landed but ARCore is drifting it".
    this.sampleFirstAnchorDrift()

    // Gate 'ready' on the first VALID hit-test result (ARCore has
    // detected a surface plane and the camera-forward ray hit it).
    // The coaching overlay stays up until this fires — the user is
    // told to "move slowly to scan the surface" until ARCore has an
    // actual plane it can raycast onto.
    if (!this.hasFirstHit && this.cachedHit != null) {
      this.hasFirstHit = true
      this.onSessionState?.(arSessionState.ready())
    }

    this.onUpdateCb?.()

    if (
      this.webglRenderer != null &&
      this.threeScene != null &&
      this.threeCamera != null
    ) {
      this.webglRenderer.render(this.threeScene, this.threeCamera)
    }
  }

  /** Snapshots frame.detectedPlanes into a serialisable state array
   *  that HUD + visualisation code can consume. No-op when the
   *  plane-detection feature isn't available on this device/browser
   *  (frame.detectedPlanes will be undefined) — but ALWAYS records
   *  the runtime type on sessionInfo so the HUD can show what shape
   *  the API returned. */
  private updateDetectedPlanes(frame: XRFrame): void {
    const detected = (frame as unknown as { detectedPlanes?: unknown })
      .detectedPlanes
    const currentType = describeDetectedPlanesType(detected)
    const typeChanged = this.sessionInfo.detectedPlanesType !== currentType
    if (typeChanged) {
      this.sessionInfo = {
        ...this.sessionInfo,
        detectedPlanesType: currentType,
      }
      // eslint-disable-next-line no-console
      console.log(
        '[webxr-driver] frame.detectedPlanes type →',
        currentType,
        '(raw value:',
        detected,
        ')',
      )
    }
    if (!(detected instanceof Set)) {
      // No plane-detection API on this device — but still notify when
      // the type changes so the HUD shows the updated diagnostic.
      // Bump the snapshot reference so useSyncExternalStore actually
      // re-renders (it dedupes on reference equality; without the new
      // array the HUD would never pick up the sessionInfo change here).
      if (typeChanged) {
        this.detectedPlanesSnapshot = []
        this.notifyPlaneListeners()
      }
      return
    }
    if (this.referenceSpace == null) return

    const snapshot: DetectedPlaneInfo[] = []
    for (const plane of detected as Set<XRPlane>) {
      const pose = frame.getPose(plane.planeSpace, this.referenceSpace)
      if (pose == null) continue
      // Assign a stable short id the first time we see this plane.
      let id = this.planeIdByRef.get(plane)
      if (id == null) {
        this.planeIdCounter += 1
        id = 'P' + this.planeIdCounter
        this.planeIdByRef.set(plane, id)
      }
      const polygon = plane.polygon.map((p) => ({ x: p.x, z: p.z }))
      snapshot.push({
        id,
        orientation: plane.orientation,
        position: {
          x: pose.transform.position.x,
          y: pose.transform.position.y,
          z: pose.transform.position.z,
        },
        quaternion: {
          x: pose.transform.orientation.x,
          y: pose.transform.orientation.y,
          z: pose.transform.orientation.z,
          w: pose.transform.orientation.w,
        },
        polygon,
        areaM2: shoelaceArea(polygon),
      })
    }
    // Sort by area descending — largest planes first for HUD legibility.
    snapshot.sort((a, b) => b.areaM2 - a.areaM2)
    this.detectedPlanesSnapshot = snapshot
    this.notifyPlaneListeners()
  }

  private notifyPlaneListeners(): void {
    for (const listener of Array.from(this.planeListeners)) listener()
  }

  async stop(): Promise<void> {
    if (!this.started) return
    if (this.session != null) {
      await this.session.end()
    }
    this.session = null
    this.hitTestSource = null
    this.groundHitTestSource = null
    this.cachedGroundHit = null
    this.referenceSpace = null
    this.cachedHit = null
    this.cachedHitSurfaceNormal = null
    this.cachedDepthChord = null
    this.hasFirstHit = false
    this.started = false
  }

  /** WebXR hit-test is frame-bound; we serve the most recent viewer-
   *  centered hit. The screen-point arg is ignored because the preview
   *  wheel UX always targets viewport center — the user aims with the
   *  phone, not by tapping screen edges. */
  hitTest(_point: ScreenPoint): HitResult | null {
    return this.cachedHit
  }

  /** Latest hit sample from the downward-angled hit-test source (used
   *  by the ?groundLine feature to freeze a ground Y coordinate on
   *  the first tap). Returns null when the browser did not create the
   *  angled source or the last frame produced no hit. */
  getGroundHit(): HitResult | null {
    return this.cachedGroundHit
  }

  /** Latest usable depth-map center sample as a world-space hit, or
   *  null when depth sensing is off / not granted / had no usable
   *  sample this frame. Consumed by the renderer's ?depthPlace path
   *  via chooseDepthPlacement. */
  getDepthHit(): HitResult | null {
    return this.cachedDepthHit
  }

  /** ?surfaceYaw — latest depth neighbourhood chord, or null. */
  getDepthChord(): {
    left: { x: number; y: number; z: number }
    right: { x: number; y: number; z: number }
  } | null {
    return this.cachedDepthChord
  }

  /** ?surfaceYaw — the real world normal of the current plane hit, or
   *  null before the first hit / after teardown. */
  getSurfaceHitNormal(): { x: number; y: number; z: number } | null {
    return this.cachedHitSurfaceNormal
  }

  /** ?surfaceYaw — two extra depth pixels either side of center,
   *  unprojected to WORLD points through the view's real frustum.
   *  Null when either side is unusable (dropout, out of range) — a
   *  half-chord is noise, not surface data. Never throws: a depth
   *  buffer edge case must not take down the frame loop. */
  private buildDepthChord(
    depthInfo: { getDepthInMeters: (x: number, y: number) => number },
    viewerPose: XRViewerPose,
  ): {
    left: { x: number; y: number; z: number }
    right: { x: number; y: number; z: number }
  } | null {
    try {
      const view = viewerPose.views[0]
      if (view == null) return null
      const leftM = depthInfo.getDepthInMeters(
        0.5 - SURFACE_YAW_SAMPLE_OFFSET_NVC,
        0.5,
      )
      const rightM = depthInfo.getDepthInMeters(
        0.5 + SURFACE_YAW_SAMPLE_OFFSET_NVC,
        0.5,
      )
      if (!isUsableDepthMeters(leftM) || !isUsableDepthMeters(rightM)) {
        return null
      }
      const t = viewerPose.transform
      const shared = {
        projectionMatrix: view.projectionMatrix,
        viewPosition: { x: t.position.x, y: t.position.y, z: t.position.z },
        viewOrientation: {
          x: t.orientation.x,
          y: t.orientation.y,
          z: t.orientation.z,
          w: t.orientation.w,
        },
      }
      const left = worldPointFromDepthSample({
        nvcX: 0.5 - SURFACE_YAW_SAMPLE_OFFSET_NVC,
        nvcY: 0.5,
        depthM: leftM,
        ...shared,
      })
      const right = worldPointFromDepthSample({
        nvcX: 0.5 + SURFACE_YAW_SAMPLE_OFFSET_NVC,
        nvcY: 0.5,
        depthM: rightM,
        ...shared,
      })
      if (left == null || right == null) return null
      return { left, right }
    } catch {
      return null
    }
  }

  /** Samples the WebXR Depth API at the viewport center and refreshes
   *  cachedDepthHit + the DepthStatus diagnostics. At the center pixel
   *  the view ray IS the optical axis, so the sensed z-depth is the
   *  along-ray distance and the world point is cameraPos + forward ×
   *  depth. Returns the next DepthStatus for this frame's sessionInfo
   *  write. Browser-only (needs a live XRFrame) — the pure math it
   *  leans on lives in @/ar/depth-placement, which IS vitest-covered. */
  private sampleCenterDepth(
    frame: XRFrame,
    viewerPose: XRViewerPose | undefined,
  ): DepthStatus {
    const status = this.sessionInfo.depthStatus
    if (!this.enableDepthSensing || !status.featureGranted) return status
    let depthM: number | null = null
    // Held for the ?surfaceYaw neighbourhood samples below — same
    // frame's depth buffer, read right after the center succeeds.
    let depthInfo: { getDepthInMeters: (x: number, y: number) => number } | null =
      null
    // Why the attempt yielded nothing — surfaced on the HUD so on-device
    // triage doesn't need a USB debug session.
    let failure: string | null = null
    if (viewerPose == null) {
      failure = 'no-viewer-pose'
    } else {
      const getDepthInformation = (
        frame as unknown as {
          getDepthInformation?: (view: XRView) => {
            getDepthInMeters: (x: number, y: number) => number
          } | null
        }
      ).getDepthInformation
      const view = viewerPose.views[0]
      if (typeof getDepthInformation !== 'function') {
        failure = 'api-missing'
      } else if (view == null) {
        failure = 'no-view'
      } else {
        try {
          const info = getDepthInformation.call(frame, view)
          if (info == null) {
            // Spec-allowed per-frame absence ("depth data unavailable in
            // the current frame") — chronic on some devices.
            failure = 'info-null'
          } else {
            depthM = info.getDepthInMeters(0.5, 0.5)
            if (!isUsableDepthMeters(depthM)) {
              failure = `unusable:${depthM}`
            } else {
              depthInfo = info
            }
          }
        } catch (err) {
          failure = `threw:${String((err as any)?.message ?? err).slice(0, 60)}`
        }
      }
    }
    if (failure != null) {
      this.cachedDepthHit = null
      this.cachedDepthChord = null
      return {
        ...status,
        framesWithoutDepth: status.framesWithoutDepth + 1,
        lastFailure: failure,
      }
    }
    // failure == null implies viewerPose != null (every null path sets a
    // failure above); assert for the type-narrowing TS can't see.
    const t = viewerPose!.transform
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(
      new THREE.Quaternion(
        t.orientation.x,
        t.orientation.y,
        t.orientation.z,
        t.orientation.w,
      ),
    )
    const cameraPos = { x: t.position.x, y: t.position.y, z: t.position.z }
    const position = computeDepthWorldPoint(cameraPos, forward, depthM!)
    this.cachedDepthHit = { position, normal: { x: 0, y: 0, z: 1 } }
    this.cachedDepthChord =
      this.sampleDepthNeighborhood && depthInfo != null
        ? this.buildDepthChord(depthInfo, viewerPose!)
        : null
    // Same-frame plane-hit distance → the delta the HUD exists to show.
    let planeDist: number | null = null
    if (this.cachedHit != null) {
      const dx = this.cachedHit.position.x - cameraPos.x
      const dy = this.cachedHit.position.y - cameraPos.y
      const dz = this.cachedHit.position.z - cameraPos.z
      planeDist = Math.sqrt(dx * dx + dy * dy + dz * dz)
    }
    return {
      ...status,
      framesWithDepth: status.framesWithDepth + 1,
      lastDepthM: depthM,
      lastPlaneHitDistanceM: planeDist,
      lastDeltaM: planeDist != null ? planeDist - depthM! : null,
      lastFailure: null,
    }
  }

  /** Preferred anchor path for placement: creates an anchor
   *  ATTACHED to the underlying detected plane via
   *  XRHitTestResult.createAnchor(). Result: much more stable than
   *  the manufactured world anchor because ARCore keeps the anchor
   *  glued to the tracked plane's pose, not just to raw world
   *  coords. Returns { id, position } synchronously (id ready
   *  immediately, actual XRAnchor resolves next frame via the flush
   *  queue). null when there's no cached hit — the caller (renderer
   *  → controller) is expected to refuse placement with a user-
   *  facing toast in that case ("no plane, no placement" — iOS-style). */
  createPlaneAttachedAnchor(): {
    id: string
    position: { x: number; y: number; z: number }
  } | null {
    if (this.referenceSpace == null) return null
    if (this.cachedHit == null) return null
    this.anchorIdCounter += 1
    const id = 'anchor-' + this.anchorIdCounter
    this.pendingAnchorRequests.push({ kind: 'plane', id })
    this.bumpAnchorStatus({
      created: this.sessionInfo.anchorStatus.created + 1,
    })
    if (this.firstAnchorId == null) {
      this.firstAnchorId = id
      this.bumpAnchorStatus({
        firstAnchorInitialPos: {
          x: this.cachedHit.position.x,
          y: this.cachedHit.position.y,
          z: this.cachedHit.position.z,
        },
      })
    }
    // eslint-disable-next-line no-console
    console.log(
      '[webxr-driver] createPlaneAttachedAnchor queued',
      id,
      'at',
      this.cachedHit.position,
    )
    return { id, position: this.cachedHit.position }
  }

  /** Called by the coaching-overlay "Place anyway" fallback after
   *  the PLACE_ANYWAY_UNLOCK_MS timeout. Forces the ready() state
   *  transition so the user can proceed to placement even when
   *  ARCore hasn't produced a hit — poor lighting / plain surfaces /
   *  glare. If no cachedHit exists yet, seeds one at 2m along
   *  camera-forward so placeWheel() has something to work with. */
  forceReady(): void {
    if (this.hasFirstHit) return
    if (this.cachedHit == null && this.threeCamera != null) {
      // Seed a placement point 2m ahead of the camera — pure fallback
      // for the case where the user opts in via the coaching overlay's
      // "Place anyway" button. Anchors will be less stable without a
      // real detected surface, but the user has explicitly consented.
      const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(
        this.threeCamera.quaternion,
      )
      const camPos = this.threeCamera.getWorldPosition(new THREE.Vector3())
      this.cachedHit = {
        position: {
          x: camPos.x + forward.x * 2,
          y: camPos.y + forward.y * 2,
          z: camPos.z + forward.z * 2,
        },
        normal: { x: 0, y: 0, z: 1 },
      }
    }
    this.hasFirstHit = true
    this.onSessionState?.(arSessionState.ready())
  }

  /** Asks ARCore to anchor `hit`'s world position. Called from tap
   *  handlers that run OUTSIDE the XR frame callback — so we can NOT
   *  call frame.createAnchor here; the WebXR runtime throws
   *  InvalidStateError on any XRFrame accessed outside its producing
   *  callback. Instead we queue the request; onFrame drains the queue
   *  on the next frame with a live XRFrame. Returns the anchor id
   *  synchronously so the renderer can pin it to the placed node
   *  immediately; getAnchorPosition returns null until the async
   *  creation resolves (~1 frame later). */
  createAnchor(hit: HitResult): string | null {
    if (this.referenceSpace == null) return null
    this.anchorIdCounter += 1
    const id = 'anchor-' + this.anchorIdCounter
    this.pendingAnchorRequests.push({ kind: 'world', id, hit })
    this.bumpAnchorStatus({ created: this.sessionInfo.anchorStatus.created + 1 })
    if (this.firstAnchorId == null) {
      this.firstAnchorId = id
      this.bumpAnchorStatus({
        firstAnchorInitialPos: {
          x: hit.position.x,
          y: hit.position.y,
          z: hit.position.z,
        },
      })
    }
    // eslint-disable-next-line no-console
    console.log('[webxr-driver] createAnchor queued', id, 'at', hit.position)
    return id
  }

  /** Drains pendingAnchorRequests inside onFrame — the only place a
   *  live XRFrame is guaranteed to be safe for createAnchor. Called
   *  with the current frame BEFORE any other frame-scoped work so
   *  fresh anchors are ready for the same frame's tick. */
  private flushPendingAnchorRequests(frame: XRFrame): void {
    if (this.pendingAnchorRequests.length === 0) return
    const refSpace = this.referenceSpace
    if (refSpace == null) return
    const create = (frame as unknown as {
      createAnchor?: (pose: XRRigidTransform, space: XRReferenceSpace) => Promise<XRAnchor>
    }).createAnchor
    if (typeof create !== 'function') {
      // 'anchors' feature not actually available on this runtime.
      // Mark every pending as rejected so the HUD reflects reality
      // and drop the queue.
      for (const _ of this.pendingAnchorRequests) {
        this.bumpAnchorStatus({
          rejected: this.sessionInfo.anchorStatus.rejected + 1,
        })
      }
      this.pendingAnchorRequests = []
      return
    }
    const XRRigidTransformCtor = (globalThis as unknown as {
      XRRigidTransform?: new (
        position: DOMPointInit,
        orientation: DOMPointInit,
      ) => XRRigidTransform
    }).XRRigidTransform
    if (XRRigidTransformCtor == null) return
    const queue = this.pendingAnchorRequests
    this.pendingAnchorRequests = []
    for (const req of queue) {
      let anchorPromise: Promise<XRAnchor> | null = null
      if (req.kind === 'plane' && this.hitTestSource != null) {
        // Plane-attached anchor path — get a fresh XRHitTestResult
        // from the current frame and call its .createAnchor(). The
        // resulting anchor is attached to the underlying tracked
        // plane, so ARCore keeps it stable as the world model
        // updates. Falls through to null (no anchor) if the current
        // frame has no hits — user has aimed off-plane between tap
        // and the next XR frame; the placed wheel then hides via
        // the syncNodeToAnchor visibility gate.
        const freshHits = frame.getHitTestResults(this.hitTestSource)
        const firstHit = freshHits[0] as
          | (XRHitTestResult & { createAnchor?: () => Promise<XRAnchor> })
          | undefined
        if (firstHit != null && typeof firstHit.createAnchor === 'function') {
          anchorPromise = firstHit.createAnchor()
        }
      } else if (req.kind === 'world') {
        const pose = new XRRigidTransformCtor(
          { x: req.hit.position.x, y: req.hit.position.y, z: req.hit.position.z, w: 1 },
          { x: 0, y: 0, z: 0, w: 1 },
        )
        anchorPromise = create.call(frame, pose, refSpace)
      }

      if (anchorPromise == null) {
        // Nothing to attach — mark as rejected so the HUD reflects it.
        this.bumpAnchorStatus({
          rejected: this.sessionInfo.anchorStatus.rejected + 1,
        })
        // eslint-disable-next-line no-console
        console.warn(
          '[webxr-driver] createAnchor: no plane hit available on flush',
          req.id,
        )
        continue
      }

      anchorPromise
        .then((anchor: XRAnchor) => {
          this.anchors.set(req.id, anchor)
          this.bumpAnchorStatus({
            landed: this.sessionInfo.anchorStatus.landed + 1,
          })
          // eslint-disable-next-line no-console
          console.log(
            '[webxr-driver] createAnchor RESOLVED',
            req.id,
            req.kind,
          )
        })
        .catch((err: unknown) => {
          this.bumpAnchorStatus({
            rejected: this.sessionInfo.anchorStatus.rejected + 1,
          })
          // eslint-disable-next-line no-console
          console.warn('[webxr-driver] createAnchor REJECTED', req.id, err)
        })
    }
  }

  /** Helper: patch anchor-status fields on sessionInfo and fire the
   *  plane-listener chain so the HUD re-renders. Bumps snapshot ref
   *  the same way surface classification does. */
  private bumpAnchorStatus(patch: Partial<AnchorStatus>): void {
    this.sessionInfo = {
      ...this.sessionInfo,
      anchorStatus: { ...this.sessionInfo.anchorStatus, ...patch },
    }
    this.detectedPlanesSnapshot = [...this.detectedPlanesSnapshot]
    this.notifyPlaneListeners()
  }

  getAnchorPosition(id: string): { x: number; y: number; z: number } | null {
    const anchor = this.anchors.get(id)
    if (anchor == null) return null
    const frame = this.lastFrame
    const refSpace = this.referenceSpace
    if (frame == null || refSpace == null) return null
    const pose = frame.getPose(anchor.anchorSpace, refSpace)
    if (pose == null) return null
    return {
      x: pose.transform.position.x,
      y: pose.transform.position.y,
      z: pose.transform.position.z,
    }
  }

  private sampleFirstAnchorDrift(): void {
    if (this.firstAnchorId == null) return
    const status = this.sessionInfo.anchorStatus
    const initial = status.firstAnchorInitialPos
    if (initial == null) return
    const pos = this.getAnchorPosition(this.firstAnchorId)
    if (pos == null) {
      // Only bump the lost-frames counter if the anchor has landed —
      // pre-resolution null is expected, not a signal.
      if (this.anchors.has(this.firstAnchorId)) {
        this.bumpAnchorStatus({
          poseLostFrames: status.poseLostFrames + 1,
        })
      }
      return
    }
    const dx = pos.x - initial.x
    const dy = pos.y - initial.y
    const dz = pos.z - initial.z
    const drift = Math.sqrt(dx * dx + dy * dy + dz * dz)
    // Only bump when drift changes materially — avoids a HUD re-render
    // every single frame from floating-point wobble.
    if (
      status.firstAnchorCurrentPos == null ||
      Math.abs(drift - status.firstAnchorDriftMeters) > 0.001
    ) {
      this.bumpAnchorStatus({
        firstAnchorCurrentPos: pos,
        firstAnchorDriftMeters: drift,
      })
    }
  }

  releaseAnchor(id: string): void {
    const anchor = this.anchors.get(id)
    if (anchor == null) return
    this.anchors.delete(id)
    try {
      anchor.delete()
    } catch {
      // Anchor already gone (session ended) — best-effort cleanup.
    }
  }
}

/** One grep-able DevTools line per second with every convergence + depth
 *  signal (2026-08-20 LUAT instrument). Pure — testable without WebXR.
 *  The A/B protocol: run the same scene with and without the depth flags
 *  and diff these lines; `hits=` flatlining at 0 only when depth is
 *  granted convicts the session-level feature request. */
export function formatWebXrDiagLine(
  info: WebXRSessionInfo,
  fps: number,
): string {
  const d = info.depthStatus
  const depth = !d.requested
    ? 'off'
    : !d.featureGranted
      ? 'requested-not-granted'
      : `granted ${d.depthUsage ?? '?'}/${d.depthDataFormat ?? '?'} ` +
        `${d.framesWithDepth}/${d.framesWithoutDepth}` +
        (d.lastFailure != null ? ` fail=${d.lastFailure}` : '')
  // First placed anchor's cumulative displacement from its initial pose
  // — ARCore drifting the anchor is a placement-error source distinct
  // from tap-time measurement error. Guarded: test stubs may omit it.
  const drift =
    info.anchorStatus?.firstAnchorInitialPos != null
      ? ` drift=${info.anchorStatus.firstAnchorDriftMeters.toFixed(2)}m`
      : ''
  return (
    `[webxr-diag] t=${(info.elapsedMs / 1000).toFixed(1)}s ` +
    `fps=${fps.toFixed(1)} ` +
    `poses=${info.viewerPoseFrames} ` +
    `hits=${info.hitTestFramesWithResult}/${info.hitTestFramesEmpty} ` +
    `planes=${info.detectedPlanesType} ` +
    `surface=${info.currentSurface} ` +
    `depth=${depth}${drift}`
  )
}

/** Turns the runtime value of frame.detectedPlanes into a short string
 *  the HUD can display. Answers "did this Chrome/ARCore give us the
 *  plane-detection API at all, and if so what's in it?" */
function describeDetectedPlanesType(value: unknown): string {
  if (value === undefined) return 'undefined (API not implemented)'
  if (value === null) return 'null'
  if (value instanceof Set) return `Set(${value.size})`
  if (typeof value === 'object') {
    const ctorName =
      (value?.constructor && value.constructor.name) || 'Object'
    return `${ctorName}`
  }
  return typeof value
}

/** 3D cross product a × b. Used to build the anchored-wall tangent
 *  frame's "right" axis from gravityUp and the plane normal. */
function crossVec3(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  }
}

/** Shoelace formula on a 2D polygon (XZ plane). Returns absolute area
 *  in whatever units the input vertices use — meters² for ARCore polygons. */
function shoelaceArea(polygon: ReadonlyArray<{ x: number; z: number }>): number {
  const n = polygon.length
  if (n < 3) return 0
  let sum = 0
  for (let i = 0; i < n; i++) {
    const a = polygon[i]
    const b = polygon[(i + 1) % n]
    sum += a.x * b.z - b.x * a.z
  }
  return Math.abs(sum) / 2
}

/** Given the viewer pose orientation quaternion, decide whether the
 *  phone is physically in landscape. Projects world +Y (gravity-up)
 *  onto the camera's local +Y (screen-top) and +X (screen-right) axes;
 *  whichever's |component| is larger wins.
 *
 *  Portrait (roll ≈ 0): local +Y ≈ world +Y → up-component big, right-
 *    component ≈ 0.
 *  Landscape (roll ≈ ±90°): local +X (or -X) ≈ world +Y → right-
 *    component big, up-component ≈ 0.
 *
 *  This isolates roll (phone rotation about camera forward axis) and
 *  is invariant to pitch (looking up/down) and yaw (looking left/
 *  right) — exactly the property we need to distinguish "phone held
 *  vertically vs. sideways." */
export function deriveViewerIsLandscape(q: {
  x: number
  y: number
  z: number
  w: number
}): boolean {
  const quadrant = deriveViewerRollQuadrant(q)
  return quadrant === 90 || quadrant === 270
}

/** How far the phone is rolled about the camera's forward axis, snapped
 *  to the nearest quarter turn.
 *
 *  0 = upright portrait. 90 = the phone's top edge points to the user's
 *  LEFT. 180 = upside-down portrait. 270 = top edge points to the
 *  user's RIGHT. The convention deliberately matches
 *  `screen.orientation.angle`, so the two can be subtracted to get the
 *  rotation BETWEEN the DOM frame and the device frame — which is what
 *  a pointer delta actually has to be corrected by.
 *
 *  Method: project world +Y (gravity-up) onto the camera's local +Y
 *  (screen-top) and +X (screen-right) axes and take the angle between
 *  them. That isolates roll and is invariant to pitch and yaw, which is
 *  exactly the property needed to tell "held sideways" from "looking at
 *  the floor".
 *
 *  `deriveViewerIsLandscape` is defined in terms of this rather than
 *  computed separately, so the two answers cannot drift apart. The two
 *  are equivalent by construction: rounding the angle to ±90 happens
 *  precisely when the right-component dominates the up-component. */
export function deriveViewerRollQuadrant(q: {
  x: number
  y: number
  z: number
  w: number
}): 0 | 90 | 180 | 270 {
  // Y component of q · (0,1,0) · q⁻¹ — the world-space Y coordinate
  // of the camera's local +Y axis.
  const upDotWorldY = 1 - 2 * (q.x * q.x + q.z * q.z)
  // Y component of q · (1,0,0) · q⁻¹ — the world-space Y coordinate
  // of the camera's local +X axis.
  const rightDotWorldY = 2 * (q.x * q.y + q.z * q.w)
  const degrees = (Math.atan2(rightDotWorldY, upDotWorldY) * 180) / Math.PI
  const snapped = ((Math.round(degrees / 90) * 90) % 360 + 360) % 360
  return snapped as 0 | 90 | 180 | 270
}
